import { LoadingScreen } from "components/LoadingScreen";
import { toastErro } from "components/Toast";
import { useEffect, useMemo, useState } from "react";
import {
  ILancamentoCriarLote,
  IProcessoEmLote,
} from "../../interface/IProcessoEmLote";
import { useCriarLote } from "../../hooks/useCriarLote";
import { FormCriarLote } from "./FormCriarLote";
import { ListaProcessosLote } from "./ListaProcessosLote";
import { ModalAdicionarCusto } from "./ModalAdicionarCusto";
import { ModalSelecionarProcessos } from "./ModalSelecionarProcessos";
import { ModalCancelarLote } from "./ModalCancelarLote";
import { ModalSucessoCriarLote } from "./ModalSucessoCriarLote";

export interface IFormCriarLote {
  idBeneficiario: number;
  numeroNfRecibo: string;
  valorNfRecibo: string;
  arquivoRecibo: File | null;
  arquivosBoleto: File[];
  observacao: string;
  pagamentoUrgente: boolean;
}

interface IProps {
  onCancelar: () => void;
  onSalvar: () => void;
}

export function CriarLote({ onCancelar, onSalvar }: IProps) {
  const {
    loading,
    setLoading,
    optionsBeneficiarios,
    carregarBeneficiarios,
    processosVincular,
    setProcessosVincular,
    selectedRowsVincular,
    setSelectedRowsVincular,
    clearRowsVincular,
    setClearRowsVincular,
    buscarProcessosVincular,
    validarProcessos,
    validarPlacasChassis,
    optionsDescricao,
    carregarDescricoes,
    buscarServicosAReceber,
    salvarLote,
    enviarBoleto,
    onClickProcesso,
  } = useCriarLote();

  const [processos, setProcessos] = useState<IProcessoEmLote[]>([]);
  const [selectedRowsTabela, setSelectedRowsTabela] = useState<
    IProcessoEmLote[]
  >([]);
  const [clearRowsTabela, setClearRowsTabela] = useState(false);
  const [formData, setFormData] = useState<IFormCriarLote>({
    idBeneficiario: -1,
    numeroNfRecibo: "",
    valorNfRecibo: "",
    arquivoRecibo: null,
    arquivosBoleto: [],
    observacao: "",
    pagamentoUrgente: false,
  });

  const [showModalSelecionar, setShowModalSelecionar] = useState(false);
  const [showModalAdicionarCusto, setShowModalAdicionarCusto] = useState(false);
  const [showModalAdicionarCustoTabela, setShowModalAdicionarCustoTabela] =
    useState(false);
  const [showModalCancelar, setShowModalCancelar] = useState(false);
  const [showModalSucesso, setShowModalSucesso] = useState(false);
  const [idLoteCriado, setIdLoteCriado] = useState<number | null>(null);

  useEffect(() => {
    carregarBeneficiarios();
  }, []);

  useEffect(() => {
    if (formData.idBeneficiario >= 0) {
      carregarDescricoes(formData.idBeneficiario);
    }
  }, [formData.idBeneficiario]);

  const custosKey = useMemo(
    () =>
      processos
        .map(
          (p) =>
            `${p.idProcesso}:${[...new Set(p.custos.map((c) => c.idDescricao))]
              .sort((a, b) => a - b)
              .join(",")}`,
        )
        .join("|"),
    [processos],
  );

  useEffect(() => {
    const pares = processos.flatMap((p) =>
      [...new Set(p.custos.map((c) => c.idDescricao))].map((idDescricao) => ({
        idProcesso: p.idProcesso,
        idDescricao,
      })),
    );
    if (!pares.length) return;

    let ativo = true;
    buscarServicosAReceber(pares).then((linhas) => {
      if (!ativo) return;
      const mapa = new Map(linhas.map((l) => [l.idProcesso, l.aReceber]));
      setProcessos((prev) =>
        prev.map((p) => ({ ...p, aReceber: mapa.get(p.idProcesso) ?? null })),
      );
    });
    return () => {
      ativo = false;
    };
  }, [custosKey]);

  const onSubmitAdicionarCusto = (custo: {
    idDescricao: number;
    label: string;
    valor: string;
    observacao: string;
  }) => {
    const rowsParaAdicionar = showModalAdicionarCusto
      ? selectedRowsVincular
      : selectedRowsTabela;

    setProcessos((atual) => {
      const mapa = new Map(atual.map((p) => [p.idProcesso, p]));

      for (const row of rowsParaAdicionar) {
        const processoExistente = mapa.get(row.idProcesso);

        if (processoExistente) {
          const indice = processoExistente.custos.findIndex(
            (c) => c.idDescricao === custo.idDescricao,
          );
          const novosCustos =
            indice !== -1
              ? processoExistente.custos.map((c, i) =>
                  i === indice
                    ? { ...c, valor: custo.valor, observacao: custo.observacao }
                    : c,
                )
              : [...processoExistente.custos, custo];

          mapa.set(row.idProcesso, {
            ...processoExistente,
            custos: novosCustos,
          });
        } else {
          mapa.set(row.idProcesso, {
            idProcesso: row.idProcesso,
            placa: row.placa ?? null,
            renavam: row.renavam ?? null,
            chassi: row.chassi ?? null,
            nomeEmpresa: row.nomeEmpresa ?? null,
            nomeDivisao: row.nomeDivisao ?? null,
            nomeCentroCusto: row.nomeCentroCusto ?? null,
            servico: row.servico ?? null,
            custos: [custo],
            aReceber: row.aReceber ?? null,
          });
        }
      }

      return Array.from(mapa.values());
    });

    setShowModalAdicionarCusto(false);
    setShowModalAdicionarCustoTabela(false);
    setShowModalSelecionar(false);
    setSelectedRowsVincular([]);
    setProcessosVincular([]);
  };

  const onRemoverProcessos = () => {
    setProcessos((atual) =>
      atual.filter(
        (p) => !selectedRowsTabela.some((r) => r.idProcesso === p.idProcesso),
      ),
    );
    setClearRowsTabela(true);
    setSelectedRowsTabela([]);
  };

  useEffect(() => {
    if (clearRowsTabela) setClearRowsTabela(false);
  }, [clearRowsTabela]);

  const onSalvarLote = async () => {
    if (!processos.length) {
      toastErro("Adicione pelo menos um processo ao lote");
      return;
    }

    const lancamentos: ILancamentoCriarLote[] = processos.flatMap((p) =>
      p.custos.map((c) => ({
        idProcesso: p.idProcesso,
        idDescricao: c.idDescricao,
        valor: c.valor,
        observacao: c.observacao,
      })),
    );

    const { hasErro, data } = await salvarLote({
      idBeneficiario: formData.idBeneficiario,
      numeroNfRecibo: formData.numeroNfRecibo,
      valorNfRecibo: formData.valorNfRecibo,
      arquivoRecibo: formData.arquivoRecibo,
      observacao: formData.observacao,
      pagamentoUrgente: formData.pagamentoUrgente,
      lancamentos,
    });

    if (hasErro) {
      toastErro(data?.message ?? "Erro ao criar lote de pagamento");
      return;
    }

    const idLote: number | undefined = data?.idLote;

    if (formData.arquivosBoleto.length) {
      if (idLote) {
        setLoading(true);
        for (const arquivo of formData.arquivosBoleto) {
          const result = await enviarBoleto(idLote, arquivo);
          if (result?.hasErro) {
            toastErro(`Falha ao enviar boleto: ${arquivo.name}`);
          }
        }
        setLoading(false);
      } else {
        toastErro(
          "Lote criado, mas não foi possível anexar os boletos: ID do lote não retornado",
        );
      }
    }

    setIdLoteCriado(idLote ?? null);
    setShowModalSucesso(true);
  };

  const totalCustos = processos.reduce(
    (acc, p) => acc + p.custos.reduce((s, c) => s + Number(c.valor || 0), 0),
    0,
  );

  const valorRecibo = Number(
    formData.valorNfRecibo?.replace(/[^0-9.]/g, "") || 0,
  );

  const valorDivergente =
    processos.length > 0 &&
    valorRecibo > 0 &&
    Math.round(totalCustos * 100) !== Math.round(valorRecibo * 100);

  return (
    <>
      <LoadingScreen mostrar={loading} zIndex={2000} />

      <FormCriarLote
        optionsBeneficiarios={optionsBeneficiarios}
        formData={formData}
        setFormData={setFormData}
        onCancelar={() => setShowModalCancelar(true)}
        onSelecionarProcessos={() => setShowModalSelecionar(true)}
        onSalvarLote={onSalvarLote}
        disabledSalvar={!processos.length}
        valorDivergente={valorDivergente}
        bloquearBeneficiario={!!processos.length}
      />

      <ListaProcessosLote
        processos={processos}
        setProcessos={setProcessos}
        selectedRows={selectedRowsTabela}
        setSelectedRows={setSelectedRowsTabela}
        clearRows={clearRowsTabela}
        totalCustos={totalCustos}
        valorDivergente={valorDivergente}
        onRemoverProcessos={onRemoverProcessos}
        onAdicionarCusto={() => {
          if (!selectedRowsTabela.length) {
            toastErro("Selecione pelo menos um processo");
            return;
          }
          setShowModalAdicionarCustoTabela(true);
        }}
        onSelecionarProcessos={() => setShowModalSelecionar(true)}
        onClickProcesso={onClickProcesso}
      />

      <ModalSelecionarProcessos
        show={showModalSelecionar}
        handleClose={() => {
          setShowModalSelecionar(false);
          setProcessosVincular([]);
          setSelectedRowsVincular([]);
        }}
        processosVincular={processosVincular}
        selectedRows={selectedRowsVincular}
        setSelectedRows={setSelectedRowsVincular}
        clearRows={clearRowsVincular}
        onBuscar={buscarProcessosVincular}
        onAdicionarCusto={() => {
          if (!selectedRowsVincular.length) {
            toastErro("Selecione pelo menos um processo");
            return;
          }
          setShowModalAdicionarCusto(true);
        }}
        validarProcessos={validarProcessos}
        validarPlacasChassis={validarPlacasChassis}
        onClickProcesso={onClickProcesso}
      />

      <ModalAdicionarCusto
        show={showModalAdicionarCusto || showModalAdicionarCustoTabela}
        handleClose={() => {
          setShowModalAdicionarCusto(false);
          setShowModalAdicionarCustoTabela(false);
        }}
        optionsDescricao={optionsDescricao}
        idFornecedor={formData.idBeneficiario}
        carregarDescricoes={carregarDescricoes}
        onSubmit={onSubmitAdicionarCusto}
      />

      <ModalCancelarLote
        show={showModalCancelar}
        handleClose={() => setShowModalCancelar(false)}
        onConfirm={onCancelar}
      />

      <ModalSucessoCriarLote
        show={showModalSucesso}
        idLote={idLoteCriado}
        onClose={onSalvar}
      />
    </>
  );
}
